import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.ppt');
/**
 * page.ppt — PPT 편집 페이지 (리팩토링)
 *
 * 슬라이드·요소·드래그·리사이즈·Undo/Redo 로직은
 * SlideCanvasController (@wiz/libs/portal/editor-core/canvas) 에 위임한다.
 *
 * 이 컴포넌트는 UI 상태(AI 패널, 저장 상태, 프로젝트 제목)와
 * Pug 템플릿 바인딩 프록시만 담당한다.
 */
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { HistoryManager }         from "src/libs/portal/editor-core/history";
import { SlideCanvasController }  from "src/libs/portal/editor-core/canvas";

@Component({
    selector: 'wiz-page-ppt',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.ppt/view.scss */
:host {
  display: block;
  height: 100%;
  overflow: hidden;
}

.ppt-canvas {
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
}

.ppt-element {
  transition: box-shadow 0.12s;
}
.ppt-element:hover {
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.4);
}
.ppt-element.ring-2 {
  z-index: 10 !important;
}`],
})
export class PagePptComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    // ─── UI 상태 (컴포넌트 고유) ────────────────────────────────────────
    public loading: boolean = false;
    public saving: boolean = false;
    public rightPanelOpen: boolean = true;
    public aiPanelOpen: boolean = false;
    public aiLoading: boolean = false;
    public aiTopic: string = '';
    public aiSlideCount: number = 5;
    public projectTitle: string = '새 프레젠테이션';

    // ─── 모듈 ────────────────────────────────────────────────────────────
    public history = new HistoryManager<any>(50);
    public canvas!: SlideCanvasController;
    private _keyHandler!: (e: KeyboardEvent) => void;

    // ─── Pug 호환 프록시 getter/setter ──────────────────────────────────
    // 기존 Pug 템플릿이 직접 참조하는 이름을 유지한다.

    get dirty():               boolean { return this.canvas?.dirty ?? false; }
    get slides():              any[]   { return this.canvas?.slides ?? []; }
    get selectedSlideIndex():  number  { return this.canvas?.selectedSlideIndex ?? 0; }
    get selectedElementId():   any     { return this.canvas?.selectedElementId ?? null; }
    get canUndo():             boolean { return this.canvas?.canUndo ?? false; }
    get canRedo():             boolean { return this.canvas?.canRedo ?? false; }

    get currentSlide(): any  { return this.canvas?.currentSlide ?? null; }
    get selectedElement(): any { return this.canvas?.selectedElement ?? null; }

    get brand(): any {
        return this.canvas?.brand ?? { primary: '', secondary: '', text: '', bg: '', font: '', logo: '' };
    }

    // ─── Lifecycle ───────────────────────────────────────────────────────
    public async ngOnInit() {
        await this.service.init();

        this.canvas = new SlideCanvasController(
            () => this.service.render(),
            this.history,
            (msg: string) => this.service.modal.error(msg),
        );

        this._keyHandler = this.canvas.bindKeyboard();
        await this.loadProject();
        await this.service.render();
    }

    public ngOnDestroy() {
        window.removeEventListener('keydown', this._keyHandler);
        this.canvas?.destroy();
    }

    // ─── 저장 / 불러오기 ────────────────────────────────────────────────
    public async loadProject() {
        this.loading = true;
        await this.service.render();

        const { code, data } = await wiz.call('load');
        if (code === 200) {
            this.projectTitle = data.projectTitle || '새 프레젠테이션';
            this.canvas.load(data);
        }

        this.loading = false;
        this.canvas.dirty = false;
        await this.service.render();
    }

    public async saveProject() {
        this.saving = true;
        await this.service.render();

        const state = {
            ...this.canvas.toSavePayload(),
            projectTitle: this.projectTitle,
        };
        const { code } = await wiz.call('save', { state: JSON.stringify(state) });
        if (code === 200) { this.canvas.dirty = false; }

        this.saving = false;
        await this.service.render();
    }

    // ─── Undo / Redo ────────────────────────────────────────────────────
    public async undo() { await this.canvas.undo(); }
    public async redo() { await this.canvas.redo(); }

    // ─── 슬라이드 관리 ──────────────────────────────────────────────────
    public async addSlide()                        { await this.canvas.addSlide(); }
    public async deleteSlide(i: number)            { await this.canvas.deleteSlide(i); }
    public async selectSlide(i: number)            { await this.canvas.selectSlide(i); }
    public async moveSlide(from: number, dir: number) { await this.canvas.moveSlide(from, from + dir); }
    public async duplicateSlide(i: number)         { await this.canvas.duplicateSlide(i); }

    // ─── 요소 추가 ──────────────────────────────────────────────────────
    public async addTextElement()              { await this.canvas.addElement('text'); }
    public async addImageElement()             { await this.canvas.addElement('image'); }
    public async addShapeElement(shape = 'rect') { await this.canvas.addElement('shape', { shapeType: shape }); }

    // ─── 요소 선택 / 삭제 ───────────────────────────────────────────────
    public async selectElement(id: string, $event?: MouseEvent) {
        if ($event) $event.stopPropagation();
        await this.canvas.selectElement(id);
    }
    public async deselectElement()          { await this.canvas.deselectElement(); }
    public async deleteSelectedElement()    { await this.canvas.deleteSelectedElement(); }
    public async bringForward()             { if (this.canvas.selectedElementId) await this.canvas.bringForward(this.canvas.selectedElementId); }
    public async sendBackward()             { if (this.canvas.selectedElementId) await this.canvas.sendBackward(this.canvas.selectedElementId); }
    public async toggleLockElement()        { if (this.canvas.selectedElementId) await this.canvas.toggleLockElement(this.canvas.selectedElementId); }

    // ─── 속성 변경 ──────────────────────────────────────────────────────
    public async onElementPropChange() {
        this.canvas.dirty = true;
        await this.service.render();
    }

    public async onSlideBgChange(color: string) {
        await this.canvas.setSlideBackground(color);
    }

    // ─── 드래그·리사이즈 ────────────────────────────────────────────────
    public onElementMouseDown(el: any, $event: MouseEvent) {
        this.canvas.onElementMouseDown($event, el);
    }

    public onResizeMouseDown(el: any, $event: MouseEvent) {
        this.canvas.onResizeMouseDown($event, el, 'se');
    }

    // ─── AI 생성 ────────────────────────────────────────────────────────
    public async generateSlides() {
        if (!this.aiTopic.trim()) return;
        this.aiLoading = true;
        await this.service.render();

        const { code, data } = await wiz.call('ai_generate', { topic: this.aiTopic, slide_count: this.aiSlideCount });
        if (code === 200 && data?.slides) {
            this.history.push({ slides: JSON.parse(JSON.stringify(this.canvas.slides)), selectedSlideIndex: this.canvas.selectedSlideIndex, selectedElementId: null });
            for (const s of data.slides) { this.canvas.slides.push(s); }
            this.canvas.selectedSlideIndex = this.canvas.slides.length - data.slides.length;
            this.aiPanelOpen = false;
            this.canvas.dirty = true;
        }

        this.aiLoading = false;
        await this.service.render();
    }

    // ─── 브랜드 적용 ────────────────────────────────────────────────────
    public async applyBrandToCurrentSlide() {
        await this.canvas.applyBrandToCurrentSlide();
    }

    // ─── 템플릿 적용 ────────────────────────────────────────────────────
    public async applyTemplate(tplId: string) {
        const templates: any = {
            'title': {
                bg: '#ffffff',
                elements: [
                    { type: 'text', x: 45, y: 240, w: 810, h: 144, text: '프레젠테이션 제목', fontSize: 40, fontWeight: 'bold', color: '#1a1a2e', zIndex: 1, locked: false },
                    { type: 'text', x: 45, y: 414, w: 810, h: 96, text: '부제목이나 발표자 이름', fontSize: 22, color: '#4b5563', zIndex: 1, locked: false },
                ],
            },
            'content': {
                bg: '#f8fafc',
                elements: [
                    { type: 'text', x: 45, y: 36, w: 810, h: 108, text: '섹션 제목', fontSize: 30, fontWeight: 'bold', color: '#1a1a2e', zIndex: 1, locked: false },
                    { type: 'text', x: 45, y: 180, w: 495, h: 468, text: '• 내용 1\n• 내용 2\n• 내용 3', fontSize: 18, color: '#374151', zIndex: 1, locked: false },
                    { type: 'image', x: 558, y: 180, w: 315, h: 468, zIndex: 1, locked: false },
                ],
            },
            'quote': {
                bg: '#4f46e5',
                elements: [
                    { type: 'text', x: 90, y: 180, w: 720, h: 288, text: '"인용구나 핵심 메시지를 입력하세요."', fontSize: 28, fontWeight: 'bold', color: '#ffffff', textAlign: 'center', zIndex: 1, locked: false },
                    { type: 'text', x: 540, y: 504, w: 315, h: 72, text: '— 출처', fontSize: 16, color: '#e0e7ff', zIndex: 1, locked: false },
                ],
            },
            'two-col': {
                bg: '#ffffff',
                elements: [
                    { type: 'text', x: 45, y: 36, w: 810, h: 100, text: '비교 분석', fontSize: 28, fontWeight: 'bold', color: '#1a1a2e', zIndex: 1, locked: false },
                    { type: 'text', x: 27, y: 173, w: 414, h: 504, text: '왼쪽 항목\n• 내용 1\n• 내용 2', fontSize: 16, color: '#374151', zIndex: 1, locked: false },
                    { type: 'text', x: 459, y: 173, w: 414, h: 504, text: '오른쪽 항목\n• 내용 A\n• 내용 B', fontSize: 16, color: '#374151', zIndex: 1, locked: false },
                ],
            },
        };
        const tpl = templates[tplId];
        if (!tpl) return;
        await this.canvas.applyTemplate(tpl);
    }

    // ─── 스타일 헬퍼 (Pug 템플릿에서 직접 호출) ─────────────────────────
    public elementStyle(el: any): object  { return this.canvas.elementStyle(el); }
    public slideThumbStyle(slide: any): object { return this.canvas.slideThumbStyle(slide); }
}

export default PagePptComponent;